import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Search, TrendingUp, Grid, List as ListIcon } from 'lucide-react';
import { SEOMeta } from '../components/SEOMeta';
// Import the source of truth data
import stockList from '../data/stocks.json';

export const StockDirectory = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
    const [selectedLetter, setSelectedLetter] = useState<string>('ALL');

    const alphabet = ['ALL', '#', ...'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')];

    const filteredStocks = useMemo(() => {
        return stockList.filter(stock => {
            const matchesSearch =
                stock.symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
                stock.name.toLowerCase().includes(searchTerm.toLowerCase());

            if (!matchesSearch) return false;

            if (selectedLetter === 'ALL') return true;
            if (selectedLetter === '#') return /^\d/.test(stock.symbol);
            return stock.symbol.startsWith(selectedLetter);
        });
    }, [searchTerm, selectedLetter]);

    return (
        <div className="min-h-screen bg-theme-base p-6 md:p-12">
            <SEOMeta
                title="Complete Stock Directory | Stock Fortress"
                description="Browse our complete directory of analyzed stocks. Find financial projections, technical analysis, and market data for thousands of companies."
                keywords="stock directory, list of stocks, stock market list, company directory, stock fortress stocks"
                canonicalUrl="https://www.stockfortress.com/stocks/directory"
            />

            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-4xl font-bold text-theme-primary mb-4 flex items-center gap-3">
                        <TrendingUp className="w-10 h-10 text-blue-500" />
                        Stock Directory
                    </h1>
                    <p className="text-theme-muted text-lg max-w-2xl">
                        Browse our comprehensive database of companies. Access detailed financial projections,
                        valuation models, and technical analysis for every stock.
                    </p>
                </div>

                {/* Controls */}
                <div className="bg-theme-secondary p-6 rounded-xl border border-theme-tertiary mb-8 sticky top-4 z-10 shadow-lg backdrop-blur-md bg-opacity-95">
                    <div className="flex flex-col md:flex-row gap-4 mb-6">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-3 text-theme-muted w-5 h-5" />
                            <input
                                type="text"
                                placeholder="Search by symbol or company name..."
                                className="w-full pl-10 pr-4 py-3 bg-theme-base border border-theme-tertiary rounded-lg focus:ring-2 focus:ring-blue-500 text-theme-primary outline-none transition-all"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setViewMode('grid')}
                                className={`p-3 rounded-lg border transition-colors ${viewMode === 'grid'
                                        ? 'bg-blue-500/20 border-blue-500 text-blue-500'
                                        : 'border-theme-tertiary text-theme-muted hover:text-theme-primary'
                                    }`}
                            >
                                <Grid size={20} />
                            </button>
                            <button
                                onClick={() => setViewMode('list')}
                                className={`p-3 rounded-lg border transition-colors ${viewMode === 'list'
                                        ? 'bg-blue-500/20 border-blue-500 text-blue-500'
                                        : 'border-theme-tertiary text-theme-muted hover:text-theme-primary'
                                    }`}
                            >
                                <ListIcon size={20} />
                            </button>
                        </div>
                    </div>

                    {/* Alphabet Filter */}
                    <div className="flex flex-wrap gap-2">
                        {alphabet.map(letter => (
                            <button
                                key={letter}
                                onClick={() => setSelectedLetter(letter)}
                                className={`px-3 py-1 text-sm rounded-md transition-all ${selectedLetter === letter
                                        ? 'bg-blue-600 text-white font-bold shadow-lg shadow-blue-500/30'
                                        : 'bg-theme-tertiary text-theme-muted hover:bg-theme-tertiary/80 hover:text-theme-primary'
                                    }`}
                            >
                                {letter}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Results */}
                <div className="mb-4 text-theme-muted">
                    Showing {filteredStocks.length} Results
                </div>

                {viewMode === 'grid' ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                        {filteredStocks.map(stock => (
                            <Link
                                key={stock.symbol}
                                to={`/${stock.symbol}/overview`}
                                className="group p-4 bg-theme-secondary rounded-lg border border-theme-tertiary hover:border-blue-500/50 hover:bg-theme-secondary/80 transition-all hover:-translate-y-1 block"
                            >
                                <div className="flex justify-between items-start mb-2">
                                    <span className="font-bold text-lg text-theme-primary group-hover:text-blue-400 transition-colors">
                                        {stock.symbol}
                                    </span>
                                </div>
                                <div className="text-sm text-theme-muted line-clamp-1">
                                    {stock.name}
                                </div>
                            </Link>
                        ))}
                    </div>
                ) : (
                    <div className="bg-theme-secondary rounded-xl border border-theme-tertiary overflow-hidden">
                        <div className="max-h-[70vh] overflow-y-auto">
                            {filteredStocks.map((stock, i) => (
                                <Link
                                    key={stock.symbol}
                                    to={`/${stock.symbol}/overview`}
                                    className={`flex items-center justify-between p-4 hover:bg-theme-tertiary/50 transition-colors border-b border-theme-tertiary last:border-0 ${i % 2 === 0 ? 'bg-transparent' : 'bg-theme-base/20'
                                        }`}
                                >
                                    <div className="flex items-center gap-4">
                                        <span className="w-16 font-bold text-theme-primary group-hover:text-blue-400">
                                            {stock.symbol}
                                        </span>
                                        <span className="text-theme-muted">
                                            {stock.name}
                                        </span>
                                    </div>
                                    <span className="text-xs text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity">
                                        View Analysis →
                                    </span>
                                </Link>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default StockDirectory;
